import { CheckCircle2, ChevronDown, ChevronUp, Circle, Lock, RotateCcw } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { cn } from '../../../lib/utils';
import {
  ONBOARDING_STEP_IDS,
  onboardingProgress,
  useOnboardingState,
  type OnboardingState,
  type OnboardingStepId,
} from '../api/onboarding-hooks';

const STEP_LABELS: Record<OnboardingStepId, string> = {
  organization_setup: 'Workspace profile',
  sender_domain: 'Sending domain',
  sender_email: 'Sender email',
  connect_integration: 'Connection',
  import_contacts: 'Import contacts',
  test_email: 'Test email',
};

const ACTIONS: Record<OnboardingStepId, string> = {
  organization_setup: '/settings/general',
  sender_domain: '/settings/sender-domains',
  sender_email: '/onboarding?step=3',
  connect_integration: '/onboarding?step=4',
  import_contacts: '/contacts?import=1',
  test_email: '/onboarding?step=6',
};

function expandedKey(orgId: string) {
  return `engage.onboarding-banner.expanded.${orgId}`;
}

// ── Inline banner content ─────────────────────────────────────

function BannerContent({ state }: { state: OnboardingState }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = React.useState(false);
  const progress = onboardingProgress(state);
  const pct = (progress.done / progress.total) * 100;

  React.useEffect(() => {
    if (!state.organizationId) return;
    setExpanded(localStorage.getItem(expandedKey(state.organizationId)) === 'true');
  }, [state.organizationId]);

  React.useEffect(() => {
    if (!state.organizationId) return;
    localStorage.setItem(expandedKey(state.organizationId), String(expanded));
  }, [expanded, state.organizationId]);

  if (state.isComplete) return null;

  const nextStep = state.nextStep;

  return (
    <div
      className="onboarding-top-banner"
      style={{
        borderBottom: '1px solid var(--border-subtle)',
        background: 'var(--brand-subtle)',
        overflow: 'hidden',
        position: 'sticky',
        top: 0,
        zIndex: 25,
        flexShrink: 0,
        boxShadow: '0 1px 0 var(--border-subtle)',
      }}
    >
      {/* Collapsed bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '9px 24px',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded((e) => !e)}
        role="button"
        aria-expanded={expanded}
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setExpanded((v) => !v)}
      >
        {/* Progress bar */}
        <div style={{ width: 120, height: 4, borderRadius: 9999, background: 'var(--brand-muted)', overflow: 'hidden', flexShrink: 0 }}>
          <div
            style={{
              height: '100%',
              width: `${pct}%`,
              background: 'var(--brand-default)',
              borderRadius: 9999,
              transition: 'width 600ms ease',
            }}
          />
        </div>

        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-hover)' }}>
          Set up your workspace
        </span>
        <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>
          {progress.done} of {progress.total} steps complete
        </span>

        {nextStep && !expanded && (
          <>
            <span style={{ fontSize: 12, color: 'var(--text-disabled)', margin: '0 2px' }}>·</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); navigate(ACTIONS[nextStep]); }}
              style={{
                padding: '3px 10px',
                borderRadius: 6,
                border: 'none',
                background: 'var(--brand-default)',
                color: 'white',
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Continue setup →
            </button>
          </>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ color: 'var(--text-tertiary)' }}>
            {expanded
              ? <ChevronUp className="h-3.5 w-3.5" />
              : <ChevronDown className="h-3.5 w-3.5" />}
          </span>
        </div>
      </div>

      {/* Expanded step list */}
      {expanded && (
        <div style={{ padding: '4px 24px 16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {ONBOARDING_STEP_IDS.map((stepId) => {
            const completed = state.completedSteps[stepId] === true;
            const skipped   = state.skippedSteps[stepId] === true;
            const current   = state.nextStep === stepId && !completed && !skipped;

            return (
              <div
                key={stepId}
                className={cn(
                  'flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm',
                  completed && 'border-transparent bg-transparent text-text-tertiary opacity-70',
                  current   && 'border-brand-default bg-brand-subtle text-text-primary',
                  !completed && !current && !skipped && 'border-transparent bg-transparent text-text-secondary',
                  skipped   && 'border-transparent bg-transparent text-text-tertiary',
                )}
              >
                <span className="flex-none">
                  {completed ? <CheckCircle2 className="h-4 w-4 text-success-default" /> :
                   current   ? <Circle className="h-4 w-4 fill-brand-default text-brand-default" /> :
                   skipped   ? <RotateCcw className="h-4 w-4 text-warning-default" /> :
                   <Lock className="h-4 w-4 text-text-disabled" />}
                </span>
                <span className={cn('flex-1', completed && 'line-through')}>
                  {STEP_LABELS[stepId]}
                </span>
                {current && (
                  <button
                    type="button"
                    onClick={() => navigate(ACTIONS[stepId])}
                    style={{
                      padding: '3px 10px', borderRadius: 5, border: 'none',
                      background: 'var(--brand-default)', color: 'white',
                      fontSize: 11.5, fontWeight: 500, cursor: 'pointer',
                    }}
                  >
                    Set up
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Export ────────────────────────────────────────────────────

export function OnboardingBanner({ orgId }: { orgId: string | null | undefined }) {
  const query = useOnboardingState(orgId ?? null);
  if (!query.data || query.data.isComplete) return null;
  return <BannerContent state={query.data} />;
}
